import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthenticationContext } from "../../controls/auth/AuthenticationWrap";
import { UserRole } from "../../../models/User";
import {
	DebtBreadcrumb,
	DebtContainer,
	DebtTitle,
	BreadcrumbItem,
	BreadcrumbSeparator,
} from "../DebtManagement/styled/DebtManagementStyles";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import {
	AdminStationPortalLogRecord,
	GetLogsByCursorParams,
	getAdminStationPortalLogsByCursor,
} from "../../../services/StationPortalLogService";
import exportToExcel from "../MyBooking/hook/exportToExcel";
import LogTable, { getActorLabel, getStationLabel, useColumnPreferences } from "./LogTable";
import EmptyState from "./EmptyState";
import TimeRangePicker from "./TimeRangePicker";
import {
	DEFAULT_STATE,
	LogFilters,
	LogQueryState,
	RangeKey,
	countActiveFilters,
	resolveRange,
	searchToState,
	stateToSearch,
} from "./logQuery";
import { collectLogsForExport } from "./exportLogs";

const SOURCE_APP_OPTIONS = [
	{ value: "", label: "All sources" },
	// Web khách Next (whalelo.net từ 03/10/2026): thanh toán, lỗi, và từ 18/10 cả trang xem / cú bấm /
	// thời gian từng trang (page_leave). Thiếu mục này thì lọc không ra dù dữ liệu đã nằm trong bảng.
	{ value: "whalelo-web", label: "Customer web (whalelo.net)" },
	{ value: "whalelo-frontend-landing", label: "Landing (whalelo-frontend)" },
	{ value: "whalelo-frontend-admin", label: "Admin bundle (whalelo-frontend)" },
	{ value: "stations.whalelo.net-develop", label: "Station portal" },
	{ value: "whalelo-workspace", label: "Workspace" },
	{ value: "whalelo-admin", label: "Admin (standalone)" },
	// Nguồn cũ, ngừng ghi từ 2026-05-10 nhưng vẫn còn ~37k dòng trong bảng.
	// Thiếu mục này thì toàn bộ số đó lọc không ra.
	{ value: "whalelo-frontend", label: "Landing (legacy, pre-2026-05)" },
];

/**
 * Đủ 14 loại sự kiện đang thực sự có trong bảng (đếm trên bản thật 2026-08-05).
 *
 * Trước đây danh sách này chỉ có 4 mục, nên 10 loại còn lại nằm trong CSDL mà
 * lọc không ra — đáng chú ý nhất là `payment` và `debt`, đúng vùng tiền.
 * Thêm loại mới ở client thì nhớ bổ sung vào đây, nếu không nó cũng tàng hình.
 */
const EVENT_OPTIONS = [
	{ value: "", label: "All event types" },
	// Lỗi & chẩn đoán
	{ value: "api_error", label: "API error" },
	{ value: "runtime_error", label: "Runtime error" },
	{ value: "performance", label: "Performance" },
	// Hành vi người dùng
	{ value: "page_view", label: "Page view" },
	{ value: "user_interaction", label: "User interaction (click)" },
	{ value: "engagement", label: "Engagement" },
	{ value: "ux_signal", label: "UX signal (rage click)" },
	// 09/10/2026: hệ thống trả lời trạm thế nào (hộp thông báo, nút bị khoá, kết quả thao tác).
	{ value: "ui_feedback", label: "UI feedback (system response)" },
	// Nghiệp vụ
	{ value: "payment", label: "Payment" },
	{ value: "debt", label: "Debt" },
	{ value: "partner_action", label: "Station action" },
	{ value: "station_onboarding", label: "Station onboarding" },
	// Hệ thống
	{ value: "auth", label: "Auth" },
	{ value: "realtime", label: "Realtime" },
	{ value: "custom", label: "Custom" },
];

const LEVEL_OPTIONS = [
	{ value: "", label: "All levels" },
	{ value: "info", label: "Info" },
	{ value: "warn", label: "Warn" },
	{ value: "error", label: "Error" },
];

const RANGE_LABELS: Record<RangeKey, string> = {
	"15m": "15 phút gần nhất",
	"1h": "1 giờ gần nhất",
	"24h": "24 giờ gần nhất",
	"7d": "7 ngày gần nhất",
	"30d": "30 ngày gần nhất",
	custom: "khoảng thời gian tuỳ chỉnh",
};

// Ô gõ chữ chờ người dùng ngừng gõ rồi mới gọi API — gõ liên tục mà gọi ngay mỗi
// phím thì vừa tốn request vừa làm bảng nhấp nháy giữa chừng câu tìm kiếm.
const DEBOUNCE_MS = 400;

// Lấy 50 dòng mỗi lần tải (khác trần 200/mẻ của export — màn hình cần phản hồi
// nhanh, export thì ưu tiên gom nhanh cho xong).
const PAGE_LIMIT = 50;

type FetchMode = "replace" | "append";

function getInitialState(): LogQueryState {
	return searchToState(typeof window !== "undefined" ? window.location.search : "");
}

// Trần rộng nhất cho một khoảng custom — khớp đúng độ dài của lựa chọn "30 ngày
// gần nhất" (logQuery.ts). Custom cho chọn ngày tận tay nên phải chặn ở đây,
// không thì lách hẳn trần quét sâu mà logQuery.ts đã tuyên bố.
const MAX_CUSTOM_RANGE_MS = 30 * 24 * 60 * 60 * 1000;
const CLAMP_NOTE = "Khoảng tuỳ chỉnh vượt quá 30 ngày — đã tự kẹp lại còn 30 ngày gần mốc To Date nhất.";

/**
 * Ô ngày custom đến từ URL nên nội dung là bất cứ thứ gì — link chia sẻ bị sửa
 * tay, hoặc link cũ còn mang ISO đầy đủ. Chỉ kiểm rỗng là chưa đủ: `toIsoVnStart`
 * nối chuỗi mù quáng, `"abc"` thành `"abcT00:00:00.000+07:00"`, và backend
 * `normalizeDate` gặp ngày không đọc được thì trả `null` — tức là **bỏ luôn bộ
 * lọc thời gian trong im lặng** rồi quét thẳng 2,2 triệu dòng. Đúng cái lỗ vừa
 * bịt ở nhánh "custom chưa nhập ngày", chỉ vào bằng cửa khác.
 */
function isVnDateInput(value: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
	const parsed = new Date(`${value}T00:00:00.000+07:00`);
	return !Number.isNaN(parsed.getTime());
}

// Người dùng nhập/chọn ngày theo giờ Việt Nam (UTC+7, không có giờ mùa hè) —
// gắn thẳng hậu tố +07:00 khi dựng mốc ISO gửi lên, KHÔNG dùng "Z" (đó chính là
// lỗi lệch 7 tiếng đã vá). State vẫn giữ nguyên chuỗi YYYY-MM-DD người dùng gõ để
// ô input hiển thị đúng và URL chia sẻ đọc được bằng mắt.
function toIsoVnStart(dateStr: string): string {
	return `${dateStr}T00:00:00.000+07:00`;
}
function toIsoVnEnd(dateStr: string): string {
	return `${dateStr}T23:59:59.999+07:00`;
}

/** Quy đổi một mốc ISO bất kỳ ra chuỗi YYYY-MM-DD theo giờ Việt Nam — dùng để điền sẵn ô input custom. */
function toVnDateInputValue(iso: string): string {
	if (!iso) return "";
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return "";
	// Locale en-CA cho ra đúng định dạng YYYY-MM-DD.
	return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Ho_Chi_Minh" }).format(date);
}

/**
 * `range` chọn sẵn suy ra khoảng từ đồng hồ tại thời điểm gọi; `custom` dùng
 * đúng cặp mốc người dùng nhập, quy đổi sang giờ VN, và kẹp lại nếu vượt trần.
 *
 * Custom mà thiếu một trong hai ô ngày KHÔNG được trả về khoảng rỗng — rỗng
 * nghĩa là quét thẳng cả bảng. Lùi về mặc định 1 giờ gần nhất cho an toàn; đây
 * là lưới chặn cuối, độc lập với việc UI đã điền sẵn ngày khi chuyển sang Custom.
 */
function computeDateRange(state: LogQueryState, now: Date): { fromDate: string; toDate: string; clamped: boolean } {
	if (state.range === "custom") {
		if (!isVnDateInput(state.fromDate) || !isVnDateInput(state.toDate)) {
			return { ...resolveRange("1h", now), clamped: false };
		}
		const fromIso = toIsoVnStart(state.fromDate);
		const toIso = toIsoVnEnd(state.toDate);
		const spanMs = new Date(toIso).getTime() - new Date(fromIso).getTime();
		if (spanMs > MAX_CUSTOM_RANGE_MS) {
			const clampedFromIso = new Date(new Date(toIso).getTime() - MAX_CUSTOM_RANGE_MS).toISOString();
			return { fromDate: clampedFromIso, toDate: toIso, clamped: true };
		}
		return { fromDate: fromIso, toDate: toIso, clamped: false };
	}
	return { ...resolveRange(state.range, now), clamped: false };
}

function buildQueryParams(
	state: LogQueryState,
	cursor: string | undefined,
	dateRange: { fromDate: string; toDate: string }
): GetLogsByCursorParams {
	return {
		limit: PAGE_LIMIT,
		cursor,
		search: state.filters.search || undefined,
		sourceApp: state.filters.sourceApp || undefined,
		eventType: state.filters.eventType || undefined,
		level: state.filters.level || undefined,
		action: state.filters.action || undefined,
		actorEmail: state.filters.actorEmail || undefined,
		partnerId: state.filters.partnerId || undefined,
		fromDate: dateRange.fromDate || undefined,
		toDate: dateRange.toDate || undefined,
	};
}

/**
 * Chỉ dùng để dựng file Excel xuất ra — không có bản xuất tương đương trong
 * LogTable (nơi hiển thị cột chỉ format qua hàm private, không export ra ngoài
 * module đó, và việc sửa LogTable nằm ngoài ranh giới file của Task 7C).
 * Không dùng cho hiển thị trên màn hình — bảng trên màn hình do <LogTable> tự vẽ.
 */
function formatDateTimeVn(value: string | null | undefined): string {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("vi-VN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

function getStatusLabel(log: AdminStationPortalLogRecord): string {
	if (log.statusCode) return String(log.statusCode);
	if (log.level) return log.level.toUpperCase();
	return "-";
}

function toMetadataPreview(metadata: Record<string, any> | null | undefined): string {
	if (!metadata) return "";
	try {
		return JSON.stringify(metadata, null, 2);
	} catch (_err) {
		return "";
	}
}

export default function StationPortalLogsPage() {
	const authContext = useContext(AuthenticationContext);
	const { userInfo, isFinishedValidate } = authContext || {};
	const isDenied = isFinishedValidate && (!userInfo || userInfo.role !== UserRole.Admin);

	const [formState, setFormState] = useState<LogQueryState>(getInitialState);
	const [committedState, setCommittedState] = useState<LogQueryState>(getInitialState);
	const [logs, setLogs] = useState<AdminStationPortalLogRecord[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [exporting, setExporting] = useState(false);
	const [exportNote, setExportNote] = useState<string | null>(null);
	// Lỗi 5: Export bấm trong lúc ô Quick Search/Action/Actor Email còn treo
	// debounce phải bị khoá — nếu không sẽ xuất theo bộ lọc CŨ, thiếu chữ vừa gõ.
	const [searchPending, setSearchPending] = useState(false);
	// Lỗi 4: dòng giải thích khi khoảng custom bị kẹp lại còn 30 ngày.
	const [rangeClampNote, setRangeClampNote] = useState<string | null>(null);

	const { visibleColumns, toggleColumn } = useColumnPreferences();

	const debounceTimerRef = useRef<number | undefined>(undefined);
	type DateRange = { fromDate: string; toDate: string };
	const lastFetchRef = useRef<{
		state: LogQueryState;
		cursor: string | undefined;
		mode: FetchMode;
		dateRange: DateRange;
	} | null>(null);
	// Lỗi 3: cặp mốc thời gian đang có hiệu lực cho truy vấn hiện tại — chốt một
	// lần khi commit, dùng lại nguyên vẹn cho mọi lần "Tải thêm" tiếp theo, KHÔNG
	// tính lại theo đồng hồ hiện tại (đó là chuyện đã gây cửa sổ trượt).
	const activeRangeRef = useRef<DateRange>({ fromDate: "", toDate: "" });
	// Lỗi 1: đánh số thứ tự yêu cầu — một phản hồi chỉ được áp dụng (kể cả nhánh
	// lỗi và nhánh tắt loading) nếu nó vẫn là yêu cầu MỚI NHẤT đã gửi đi. Phản hồi
	// của một yêu cầu đã lỗi thời (có yêu cầu mới hơn gửi đi sau nó) bị bỏ qua
	// hoàn toàn, tránh đè dữ liệu đúng hoặc bật nhầm trạng thái rỗng/loading.
	const requestSeqRef = useRef(0);

	const fetchLogs = useCallback(
		async (state: LogQueryState, cursor: string | undefined, mode: FetchMode, dateRange: DateRange) => {
			const requestId = ++requestSeqRef.current;
			lastFetchRef.current = { state, cursor, mode, dateRange };
			const params = buildQueryParams(state, cursor, dateRange);

			if (mode === "replace") {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}
			setError(null);
			try {
				const resp = await getAdminStationPortalLogsByCursor(params);
				if (requestSeqRef.current !== requestId) return;
				setLogs((prev) => (mode === "replace" ? resp.data : prev.concat(resp.data)));
				setNextCursor(resp.nextCursor);
			} catch (err: any) {
				if (requestSeqRef.current !== requestId) return;
				setError(err?.response?.data?.message || err.message || "Không tải được log, vui lòng thử lại.");
			} finally {
				if (requestSeqRef.current !== requestId) return;
				if (mode === "replace") {
					setLoading(false);
				} else {
					setLoadingMore(false);
				}
			}
		},
		[]
	);

	// Chỉ chạy đúng một lần lúc mount, đọc trạng thái đã có trong `formState` lúc
	// khởi tạo (từ URL). Mọi thay đổi bộ lọc sau đó đi qua `commit()`, không qua
	// effect này — tránh gọi API hai lần cho cùng một thay đổi.
	useEffect(() => {
		if (isDenied) return;
		const range = computeDateRange(formState, new Date());
		activeRangeRef.current = { fromDate: range.fromDate, toDate: range.toDate };
		setRangeClampNote(range.clamped ? CLAMP_NOTE : null);
		fetchLogs(formState, undefined, "replace", activeRangeRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		return () => {
			if (debounceTimerRef.current !== undefined) {
				window.clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	const commit = useCallback(
		(next: LogQueryState) => {
			setFormState(next);
			setCommittedState(next);
			if (typeof window !== "undefined") {
				window.history.replaceState(null, "", stateToSearch(next));
			}
			setLogs([]);
			setNextCursor(null);
			// Tiện thể: đổi bộ lọc/khoảng thì dòng chú thích export cũ hết hiệu lực.
			setExportNote(null);
			// Lỗi 3: chốt cặp mốc thời gian MỘT LẦN cho truy vấn này — mọi lần "Tải
			// thêm" tiếp theo của cùng truy vấn tái dùng đúng cặp mốc ở đây, không
			// tính lại theo đồng hồ hiện tại.
			const range = computeDateRange(next, new Date());
			const dateRange: DateRange = { fromDate: range.fromDate, toDate: range.toDate };
			activeRangeRef.current = dateRange;
			setRangeClampNote(range.clamped ? CLAMP_NOTE : null);
			fetchLogs(next, undefined, "replace", dateRange);
		},
		[fetchLogs]
	);

	const applyImmediate = useCallback(
		(next: LogQueryState) => {
			if (debounceTimerRef.current !== undefined) {
				window.clearTimeout(debounceTimerRef.current);
				debounceTimerRef.current = undefined;
			}
			setSearchPending(false);
			commit(next);
		},
		[commit]
	);

	const applyDebounced = useCallback(
		(next: LogQueryState) => {
			// Vẫn cập nhật form ngay để người dùng thấy chữ mình gõ, chỉ hoãn việc gọi API.
			setFormState(next);
			setSearchPending(true);
			if (debounceTimerRef.current !== undefined) {
				window.clearTimeout(debounceTimerRef.current);
			}
			debounceTimerRef.current = window.setTimeout(() => {
				debounceTimerRef.current = undefined;
				setSearchPending(false);
				commit(next);
			}, DEBOUNCE_MS);
		},
		[commit]
	);

	const onRangeChange = (range: RangeKey) => {
		// Lỗi 4: chuyển sang custom mà hai ô ngày còn rỗng thì điền sẵn đúng cặp
		// ngày của khoảng đang có hiệu lực — không bao giờ bắn truy vấn không mốc.
		if (range === "custom" && !formState.fromDate && !formState.toDate) {
			const current = computeDateRange(formState, new Date());
			applyImmediate({
				...formState,
				range,
				fromDate: toVnDateInputValue(current.fromDate),
				toDate: toVnDateInputValue(current.toDate),
			});
			return;
		}
		applyImmediate({ ...formState, range });
	};

	const onCustomDateChange = (key: "fromDate" | "toDate", value: string) => {
		// Lỗi 2: giữ nguyên chuỗi YYYY-MM-DD người dùng chọn trong state — chỉ quy
		// đổi sang ISO kèm mốc giờ VN ở buildQueryParams/computeDateRange. Ô input
		// nhờ vậy luôn hiển thị đúng ngày đã chọn, URL chia sẻ cũng đọc được bằng mắt.
		applyImmediate({ ...formState, [key]: value });
	};

	const onSelectFilterChange = (key: keyof LogFilters, value: string) => {
		applyImmediate({ ...formState, filters: { ...formState.filters, [key]: value } });
	};

	const onTextFilterChange = (key: keyof LogFilters, value: string) => {
		applyDebounced({ ...formState, filters: { ...formState.filters, [key]: value } });
	};

	const onClearFilters = () => {
		applyImmediate({ ...formState, filters: { ...DEFAULT_STATE.filters } });
	};

	const onRetry = () => {
		const last = lastFetchRef.current;
		if (!last) return;
		fetchLogs(last.state, last.cursor, last.mode, last.dateRange);
	};

	const onLoadMore = () => {
		if (!nextCursor) return;
		// Lỗi 3: dùng lại đúng cửa sổ thời gian đã chốt cho truy vấn hiện tại.
		fetchLogs(committedState, nextCursor, "append", activeRangeRef.current);
	};

	const onExport = async () => {
		setExporting(true);
		setExportNote(null);
		try {
			const exportDateRange = computeDateRange(committedState, new Date());
			const exportParams = buildQueryParams(committedState, undefined, exportDateRange);
			delete exportParams.limit; // collectLogsForExport tự áp EXPORT_BATCH_SIZE cho từng mẻ.
			const { rows, truncated, failed } = await collectLogsForExport(exportParams, () => {});

			exportToExcel(
				rows.map((log) => ({
					"Time (VN)": formatDateTimeVn(log.occurredAt),
					Actor: getActorLabel(log),
					Station: getStationLabel(log),
					Source: log.sourceApp || "",
					"Event Type": log.eventType || "",
					Action: log.action || "",
					Level: log.level || "",
					Route: log.route || "",
					"Request Path": log.requestPath || "",
					Status: getStatusLabel(log),
					Message: log.message || "",
					Metadata: toMetadataPreview(log.metadata),
				})),
				"station-portal-logs.xlsx"
			);

			if (truncated) {
				setExportNote(
					`Đã xuất ${rows.length} dòng, còn dòng chưa lấy vì đạt trần xuất — thu hẹp bộ lọc hoặc khoảng thời gian rồi xuất tiếp.`
				);
			} else if (failed) {
				setExportNote(`Đã xuất ${rows.length} dòng trước khi gặp lỗi giữa chừng — thử xuất lại để lấy đủ.`);
			} else {
				setExportNote(`Đã xuất ${rows.length} dòng.`);
			}
		} catch (err: any) {
			setExportNote(err?.response?.data?.message || err.message || "Xuất thất bại, vui lòng thử lại.");
		} finally {
			setExporting(false);
		}
	};

	if (isDenied) {
		return (
			<MyBookingWrapper>
				<OrderInner>
					<div style={{ textAlign: "center", padding: "50px" }}>
						<h2>Access Denied</h2>
						<p>You don't have permission to access this page.</p>
					</div>
				</OrderInner>
			</MyBookingWrapper>
		);
	}

	const activeFilterCount = countActiveFilters(committedState.filters);
	const rangeLabel = RANGE_LABELS[committedState.range];
	const initialLoadFailed = Boolean(error) && logs.length === 0;
	const showEmptyState = !error && logs.length === 0;

	return (
		<DebtContainer>
			<DebtBreadcrumb>
				<BreadcrumbItem>Admin</BreadcrumbItem>
				<BreadcrumbSeparator
					src="./static/img/direction-right.png"
					alt=">"
				/>
				<BreadcrumbItem>Station Portal Logs</BreadcrumbItem>
			</DebtBreadcrumb>

			<DebtTitle>Station Portal Logs</DebtTitle>

			<div style={panelStyle}>
				<div style={{ marginBottom: 16 }}>
					<div style={labelStyle}>Time range</div>
					<TimeRangePicker
						value={formState.range}
						onChange={onRangeChange}
					/>
					{committedState.range === "custom" && rangeClampNote && (
						<div style={clampNoteStyle}>{rangeClampNote}</div>
					)}
				</div>

				<div style={filterGridStyle}>
					{formState.range === "custom" && (
						<>
							<div>
								<label
									style={labelStyle}
									htmlFor="filter-from-date">
									From Date
								</label>
								<input
									id="filter-from-date"
									type="date"
									value={formState.fromDate ? formState.fromDate.slice(0, 10) : ""}
									onChange={(event) => onCustomDateChange("fromDate", event.target.value)}
									style={inputStyle}
								/>
							</div>
							<div>
								<label
									style={labelStyle}
									htmlFor="filter-to-date">
									To Date
								</label>
								<input
									id="filter-to-date"
									type="date"
									value={formState.toDate ? formState.toDate.slice(0, 10) : ""}
									onChange={(event) => onCustomDateChange("toDate", event.target.value)}
									style={inputStyle}
								/>
							</div>
						</>
					)}

					<div>
						<label
							style={labelStyle}
							htmlFor="filter-search">
							Quick Search
						</label>
						<input
							id="filter-search"
							value={formState.filters.search}
							onChange={(event) => onTextFilterChange("search", event.target.value)}
							placeholder="message, route, action..."
							style={inputStyle}
						/>
					</div>
					<div>
						<label
							style={labelStyle}
							htmlFor="filter-source-app">
							Source App
						</label>
						<select
							id="filter-source-app"
							value={formState.filters.sourceApp}
							onChange={(event) => onSelectFilterChange("sourceApp", event.target.value)}
							style={inputStyle}>
							{SOURCE_APP_OPTIONS.map((option) => (
								<option
									key={option.value || "all"}
									value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<label
							style={labelStyle}
							htmlFor="filter-event-type">
							Event Type
						</label>
						<select
							id="filter-event-type"
							value={formState.filters.eventType}
							onChange={(event) => onSelectFilterChange("eventType", event.target.value)}
							style={inputStyle}>
							{EVENT_OPTIONS.map((option) => (
								<option
									key={option.value || "all"}
									value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<label
							style={labelStyle}
							htmlFor="filter-level">
							Level
						</label>
						<select
							id="filter-level"
							value={formState.filters.level}
							onChange={(event) => onSelectFilterChange("level", event.target.value)}
							style={inputStyle}>
							{LEVEL_OPTIONS.map((option) => (
								<option
									key={option.value || "all"}
									value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<label
							style={labelStyle}
							htmlFor="filter-action">
							Action
						</label>
						<input
							id="filter-action"
							value={formState.filters.action}
							onChange={(event) => onTextFilterChange("action", event.target.value)}
							placeholder="login_failed, logout..."
							style={inputStyle}
						/>
					</div>
					<div>
						<label
							style={labelStyle}
							htmlFor="filter-actor-email">
							Actor Email
						</label>
						<input
							id="filter-actor-email"
							value={formState.filters.actorEmail}
							onChange={(event) => onTextFilterChange("actorEmail", event.target.value)}
							placeholder="station@email.com"
							style={inputStyle}
						/>
					</div>
					<div>
						<label
							style={labelStyle}
							htmlFor="filter-partner-id">
							Partner ID
						</label>
						<input
							id="filter-partner-id"
							value={formState.filters.partnerId}
							onChange={(event) => onTextFilterChange("partnerId", event.target.value)}
							placeholder="VD: 208"
							style={inputStyle}
						/>
					</div>
					<div style={{ display: "flex", alignItems: "flex-end" }}>
						<button
							type="button"
							onClick={onClearFilters}
							style={secondaryButtonStyle}>
							Xoá bộ lọc
						</button>
					</div>
				</div>
			</div>

			<div style={panelStyle}>
				<div style={resultsHeaderStyle}>
					<div style={{ fontSize: 14, color: "#475569" }}>
						{logs.length} dòng đang hiện{activeFilterCount > 0 ? ` · đang lọc ${activeFilterCount} ô` : ""}
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{exportNote && <div style={{ fontSize: 13, color: "#475569" }}>{exportNote}</div>}
						<button
							onClick={onExport}
							disabled={exporting || loading || searchPending}
							style={primaryButtonStyle}>
							{exporting ? "Exporting..." : "Export Excel"}
						</button>
					</div>
				</div>

				{initialLoadFailed && (
					<div style={errorBannerStyle}>
						<div>{error}</div>
						<button
							type="button"
							onClick={onRetry}
							style={secondaryButtonStyle}>
							Thử lại
						</button>
					</div>
				)}

				{showEmptyState && (
					<EmptyState
						loading={loading}
						activeFilterCount={activeFilterCount}
						rangeLabel={rangeLabel}
						onClearFilters={onClearFilters}
					/>
				)}

				{logs.length > 0 && (
					<>
						<LogTable
							logs={logs}
							visibleColumns={visibleColumns}
							onToggleColumn={toggleColumn}
						/>

						{error && (
							<div style={errorBannerStyle}>
								<div>{error}</div>
								<button
									type="button"
									onClick={onRetry}
									style={secondaryButtonStyle}>
									Thử lại
								</button>
							</div>
						)}

						{!error && nextCursor && (
							<div style={loadMoreWrapStyle}>
								<button
									type="button"
									onClick={onLoadMore}
									disabled={loadingMore}
									style={secondaryButtonStyle}>
									{loadingMore ? "Đang tải…" : "Tải thêm"}
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</DebtContainer>
	);
}

const panelStyle: React.CSSProperties = {
	background: "#fff",
	borderRadius: 16,
	padding: 16,
	boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
	marginBottom: 16,
};

const filterGridStyle: React.CSSProperties = {
	display: "grid",
	gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
	gap: 12,
	alignItems: "end",
};

const resultsHeaderStyle: React.CSSProperties = {
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	marginBottom: 12,
	gap: 12,
	flexWrap: "wrap",
};

const loadMoreWrapStyle: React.CSSProperties = {
	display: "flex",
	justifyContent: "center",
	marginTop: 16,
};

const errorBannerStyle: React.CSSProperties = {
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	gap: 12,
	padding: "12px 16px",
	borderRadius: 10,
	background: "#fef2f2",
	border: "1px solid #fecaca",
	color: "#991b1b",
	fontSize: 14,
	marginBottom: 12,
};

const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, marginBottom: 6, display: "block" };

// Kẹp im lặng là không trung thực — dòng này nói rõ khoảng custom đã bị thu hẹp.
const clampNoteStyle: React.CSSProperties = {
	marginTop: 10,
	fontSize: 13,
	color: "#b45309",
	background: "#fffbeb",
	border: "1px solid #fde68a",
	borderRadius: 8,
	padding: "8px 12px",
};

const inputStyle: React.CSSProperties = {
	width: "100%",
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 12px",
	fontSize: 14,
	background: "#fff",
};

const primaryButtonStyle: React.CSSProperties = {
	border: "none",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#0f62fe",
	color: "#fff",
	fontWeight: 600,
	cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#fff",
	color: "#0f172a",
	fontWeight: 600,
	cursor: "pointer",
};
